import { useEffect, useState } from 'react'
import { FileSearch, Image, Sparkles } from 'lucide-react'
import type { FileJobState } from './fileTranslationProgress'
import { stageCopy } from './fileTranslationProgress'
import { WsButtonGhost } from './workspace'

const PROGRESS_BY_STAGE: Record<FileJobState['stage'], number> = {
  preparing: 8,
  uploading: 22,
  processing: 42,
  extracting: 48,
  translating: 72,
  finalizing: 92,
  done: 100,
  error: 100,
}

function elapsedLabel(elapsedMs: number): string {
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000))
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

/** Whole seconds since `startedAt`, re-rendering only this view once a second. */
function useElapsedMs(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (startedAt === null) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [startedAt])
  return startedAt === null ? 0 : Math.max(0, now - startedAt)
}

export function FileTranslationProgressView({
  job,
  startedAt,
  fact,
  onCancel,
}: {
  job: FileJobState
  startedAt: number | null
  fact?: string
  onCancel: () => void
}) {
  const elapsedMs = useElapsedMs(startedAt)
  const copy = stageCopy(job.stage)
  const progress = PROGRESS_BY_STAGE[job.stage]
  const Icon = job.kind === 'image' ? Image : FileSearch

  return (
    <section role="status" aria-live="polite" lang="en" dir="ltr">
      <div className="grid gap-3 items-center" style={{ gridTemplateColumns: '18px minmax(0,1fr)', padding: '10px 12px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)' }}>
        <span style={{ color: 'var(--muted-2)' }}><Icon size={16} aria-hidden /></span>
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[13px] font-medium truncate" style={{ color: 'var(--ink)' }}>{job.filename}</div>
              <div className="font-mono truncate" style={{ fontSize: 11, color: 'var(--muted-2)' }}>{copy.title} · {elapsedLabel(elapsedMs)}</div>
            </div>
          </div>
          <div className="mt-2 h-[3px] overflow-hidden rounded-full" style={{ background: 'var(--card-2)' }}>
            <div className="h-full rounded-full transition-[width] duration-500 ease-out" style={{ width: `${progress}%`, background: 'var(--accent)' }} />
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: 'var(--muted)' }}>{copy.detail}</p>
        </div>
      </div>

      {fact && (
        <div className="mt-3 flex gap-2 px-3 py-2.5 text-[12.5px] leading-relaxed" style={{ borderRadius: 6, background: 'var(--card-2)', color: 'var(--muted)' }}>
          <Sparkles size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent-deep)' }} aria-hidden />
          <span>{fact}</span>
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <WsButtonGhost h={26} onClick={onCancel}>Cancel</WsButtonGhost>
      </div>
    </section>
  )
}
